/**
 * FinancialStatementsPage — the trial balance and the income statement, with
 * the drill-down that makes them answerable.
 *
 * The point of this screen is the last click: a figure on a statement opens
 * the entries behind it, and each entry names the gift or the expense that
 * caused it. Four parallel record systems made that impossible; one ledger
 * makes it a link.
 *
 * The trial balance states whether it balances rather than quietly balancing
 * itself. If the two totals ever differ, something wrote to the ledger outside
 * the one posting path — which is precisely what a trial balance is for.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Loader2, Scale, CheckCircle2, AlertTriangle, Receipt, ArrowRight, ArrowLeft, Lock, Unlock,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import { isoDay, todayIso } from '@/lib/localDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: { ...authHeaders() } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const startOfYear = () => isoDay(new Date(new Date().getFullYear(), 0, 1));

const DrillDown = ({ account, start, end, onClose }) => {
  // A balance-sheet figure is cumulative, so its drill-down must be too —
  // otherwise the entries shown would not add up to the figure clicked.
  const from = account?._from ?? start;
  const { data, isLoading } = useQuery({
    queryKey: ['account-entries', account?.account_code, from, end],
    queryFn: () => call(
      `/api/finance/accounts/${account.account_code}/entries?start=${from}&end=${end}`),
    enabled: !!account,
  });

  return (
    <Dialog open={!!account} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="account-drilldown">
        <DialogHeader>
          <DialogTitle>
            {account?.account_code} · {account?.account_name || data?.account_name}
          </DialogTitle>
        </DialogHeader>
        {isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {data && (
          <>
            <p className="text-xs text-slate-500">
              {data.count} entries · debits {money(data.total_debit)} · credits {money(data.total_credit)}
            </p>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="py-2">Date</th>
                    <th>Entry</th>
                    <th>Description</th>
                    <th>Came from</th>
                    <th className="text-right">Debit</th>
                    <th className="text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={`${e.entry_id}-${e.debit}-${e.credit}`}
                        className={`border-t border-slate-100 ${e.reverses_entry_id ? 'text-slate-400' : ''}`}
                        data-testid={`drilldown-row-${e.entry_id}`}>
                      <td className="py-2 font-mono text-xs">
                        {e.date}
                        {e.original_date && e.original_date !== e.date && (
                          <span className="ml-1 text-[10px] text-amber-600"
                                title={`Happened ${e.original_date}, posted to the next open period`}>
                            ·{e.original_date}
                          </span>
                        )}
                      </td>
                      <td className="font-mono text-xs">{e.entry_number}</td>
                      <td className="max-w-xs truncate">{e.description}</td>
                      <td className="text-xs text-slate-500">
                        {e.source_type
                          ? String(e.source_type).replace(/_/g, ' ')
                          : <span className="italic">entered by hand</span>}
                        {e.reverses_entry_id && ' · reversal'}
                      </td>
                      <td className="text-right font-mono text-xs">{e.debit ? money(e.debit) : ''}</td>
                      <td className="text-right font-mono text-xs">{e.credit ? money(e.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.entries.length === 0 && (
                <p className="py-10 text-center text-sm text-slate-500" data-testid="drilldown-empty">
                  Nothing posted to this account in the period.
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PeriodBanner = () => {
  const { data } = useQuery({
    queryKey: ['accounting-periods'],
    queryFn: () => call('/api/finance/periods'),
  });
  const periods = data?.periods || [];
  if (!periods.length) return null;
  const closed = periods.filter((p) => p.status === 'closed').length;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2 text-xs text-slate-500"
         data-testid="period-banner">
      {closed > 0 ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
      <span>
        {closed} of {periods.length} periods closed — figures in a closed period cannot change
      </span>
      {periods.slice(0, 4).map((p) => (
        <Badge key={p.id || p._id} variant="outline"
               className={p.status === 'closed'
                 ? 'border-slate-300 bg-slate-100 text-slate-600'
                 : 'border-emerald-200 bg-emerald-50 text-emerald-700'}
               data-testid={`period-chip-${p.status}`}>
          {p.name}
        </Badge>
      ))}
    </div>
  );
};

export default function FinancialStatementsPage() {
  const [start, setStart] = useState(startOfYear());
  const [end, setEnd] = useState(todayIso());
  const [drill, setDrill] = useState(null);

  const trial = useQuery({
    queryKey: ['trial-balance', start, end],
    queryFn: () => call(`/api/finance/trial-balance?start=${start}&end=${end}`),
  });
  const income = useQuery({
    queryKey: ['income-statement', start, end],
    queryFn: () => call(`/api/finance/income-statement?start=${start}&end=${end}`),
  });
  // A position, not a period: everything posted up to the closing date counts.
  const sheet = useQuery({
    queryKey: ['balance-sheet', end],
    queryFn: () => call(`/api/finance/balance-sheet?as_of=${end}`),
  });

  const tb = trial.data;
  const is = income.data;
  const bs = sheet.data;

  const Row = ({ account, amount, right }) => (
    <button
      onClick={() => setDrill(account)}
      className="flex w-full items-center justify-between gap-4 border-t border-slate-100 py-2 text-left text-sm transition-colors hover:bg-slate-50"
      data-testid={`statement-row-${account.account_code}`}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="font-mono text-xs text-slate-400">{account.account_code}</span>
        <span className="truncate text-slate-800">{account.account_name}</span>
        <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
      </span>
      <span className="shrink-0 font-mono text-sm text-slate-900">{right ?? money(amount)}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="financial-statements-page">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <header className="mb-8 max-w-2xl">
          <a href="/admin/finance" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900" data-testid="back-to-finance">
            <ArrowLeft className="h-3 w-3" /> Finance
          </a>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
            <Receipt className="h-3.5 w-3.5" /> Financial statements
          </div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Straight from the journals
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Every figure opens the entries behind it, and every entry names the gift or
            the expense that caused it.
          </p>
        </header>

        <PeriodBanner />

        <div className="mb-8 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="tb-start" className="text-xs">From</Label>
            <Input id="tb-start" type="date" value={start} className="w-40"
                   onChange={(e) => setStart(e.target.value)} data-testid="statements-start" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tb-end" className="text-xs">To</Label>
            <Input id="tb-end" type="date" value={end} className="w-40"
                   onChange={(e) => setEnd(e.target.value)} data-testid="statements-end" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setStart(startOfYear()); setEnd(todayIso()); }}
                  data-testid="statements-reset">
            This year
          </Button>
        </div>

        <Tabs defaultValue="trial">
          <TabsList data-testid="statements-tabs">
            <TabsTrigger value="trial" data-testid="tab-trial-balance">Trial balance</TabsTrigger>
            <TabsTrigger value="income" data-testid="tab-income-statement">Income statement</TabsTrigger>
            <TabsTrigger value="balance" data-testid="tab-balance-sheet">Balance sheet</TabsTrigger>
          </TabsList>

          <TabsContent value="trial" className="mt-6">
            <Card data-testid="trial-balance-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Scale className="h-4 w-4 text-slate-400" /> Trial balance
                </CardTitle>
                <CardDescription>
                  Debits and credits by account for the period.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trial.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {trial.error && (
                  <p className="py-6 text-sm text-amber-700" data-testid="trial-balance-error">
                    {trial.error.message}
                  </p>
                )}
                {tb && (
                  <>
                    <div className={`mb-5 flex items-center gap-2 rounded-lg border p-3 text-sm ${
                      tb.balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-rose-200 bg-rose-50 text-rose-800'}`}
                         data-testid="trial-balance-verdict">
                      {tb.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      {tb.balanced
                        ? `In balance — debits and credits both ${money(tb.total_debit)}`
                        : `Out of balance by ${money(tb.difference)}. Something wrote to the ledger outside the posting path.`}
                    </div>
                    <div className="flex items-center justify-between pb-1 text-[11px] uppercase tracking-wider text-slate-400">
                      <span>Account</span><span>Debit − Credit</span>
                    </div>
                    {tb.accounts.map((a) => (
                      <Row key={a.account_code} account={a}
                           right={<>
                             <span className="text-slate-500">{money(a.debit)}</span>
                             <span className="mx-1 text-slate-300">/</span>
                             <span className="text-slate-500">{money(a.credit)}</span>
                           </>} />
                    ))}
                    {tb.accounts.length === 0 && (
                      <p className="py-10 text-center text-sm text-slate-500" data-testid="trial-balance-empty">
                        Nothing was posted in this period.
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="income" className="mt-6">
            <Card data-testid="income-statement-card">
              <CardHeader>
                <CardTitle className="text-lg">Income and expenditure</CardTitle>
                <CardDescription>Income less expenditure for the period.</CardDescription>
              </CardHeader>
              <CardContent>
                {income.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {is && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-base font-medium text-slate-700">Income</h3>
                      {is.income.map((a) => <Row key={a.account_code} account={a} amount={a.amount} />)}
                      <div className="flex items-center justify-between border-t-2 border-slate-200 py-2 text-sm font-medium">
                        <span>Total income</span>
                        <span className="font-mono" data-testid="total-income">{money(is.total_income)}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-slate-700">Expenditure</h3>
                      {is.expenditure.map((a) => <Row key={a.account_code} account={a} amount={a.amount} />)}
                      <div className="flex items-center justify-between border-t-2 border-slate-200 py-2 text-sm font-medium">
                        <span>Total expenditure</span>
                        <span className="font-mono" data-testid="total-expenditure">{money(is.total_expenditure)}</span>
                      </div>
                    </div>
                    {is.unclassified.length > 0 && (
                      <div data-testid="unclassified-accounts">
                        <h3 className="text-base font-medium text-amber-700">
                          Not yet classified
                        </h3>
                        <p className="mb-1 text-xs text-slate-500">
                          These accounts have no type on the chart of accounts, so they
                          cannot be placed. They are shown rather than dropped.
                        </p>
                        {is.unclassified.map((a) => <Row key={a.account_code} account={a} amount={a.amount} />)}
                      </div>
                    )}
                    <div className={`flex items-center justify-between rounded-lg p-4 text-base font-semibold ${
                      is.surplus >= 0 ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'}`}
                         data-testid="surplus-row">
                      <span>{is.surplus >= 0 ? 'Surplus' : 'Deficit'} for the period</span>
                      <span className="font-mono">{money(Math.abs(is.surplus))}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="balance" className="mt-6">
            <Card data-testid="balance-sheet-card">
              <CardHeader>
                <CardTitle className="text-lg">Balance sheet</CardTitle>
                <CardDescription>
                  What is owned, what is owed and what is left, as at {end}. The date
                  range above sets the closing date; a position has no start.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sheet.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {sheet.error && (
                  <p className="py-6 text-sm text-amber-700" data-testid="balance-sheet-error">
                    {sheet.error.message}
                  </p>
                )}
                {bs && (
                  <div className="space-y-8">
                    <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${
                      bs.balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-rose-200 bg-rose-50 text-rose-800'}`}
                         data-testid="balance-sheet-verdict">
                      {bs.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      {bs.balanced
                        ? `Balances — net assets and total funds are both ${money(bs.net_assets)}`
                        : `Out by ${money(bs.difference)}. Net assets and total funds should be equal.`}
                    </div>

                    <div>
                      <h3 className="text-base font-medium text-slate-700">Assets</h3>
                      {bs.assets.map((a) => <Row key={a.account_code} account={{ ...a, _from: '' }} amount={a.amount} />)}
                      <div className="flex items-center justify-between border-t-2 border-slate-200 py-2 text-sm font-medium">
                        <span>Total assets</span>
                        <span className="font-mono" data-testid="total-assets">{money(bs.total_assets)}</span>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-base font-medium text-slate-700">Liabilities</h3>
                      {bs.liabilities.map((a) => <Row key={a.account_code} account={{ ...a, _from: '' }} amount={a.amount} />)}
                      {bs.liabilities.length === 0 && (
                        <p className="py-3 text-sm text-slate-500" data-testid="no-liabilities">
                          Nothing is owed to anyone.
                        </p>
                      )}
                      <div className="flex items-center justify-between border-t-2 border-slate-200 py-2 text-sm font-medium">
                        <span>Total liabilities</span>
                        <span className="font-mono" data-testid="total-liabilities">
                          {money(bs.total_liabilities)}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-lg bg-slate-100 p-4">
                      <div className="flex items-center justify-between text-base font-semibold text-slate-900">
                        <span>Net assets</span>
                        <span className="font-mono" data-testid="net-assets">{money(bs.net_assets)}</span>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-base font-medium text-slate-700">Reserves</h3>
                      <p className="mb-2 text-xs text-slate-500">
                        Retained funds are income less expenditure to date — this ledger
                        posts no closing entries, so the figure is derived and cannot
                        disagree with the income statement.
                      </p>
                      {bs.equity.map((a) => <Row key={a.account_code} account={{ ...a, _from: '' }} amount={a.amount} />)}
                      <div className="flex items-center justify-between border-t border-slate-100 py-2 text-sm">
                        <span className="text-slate-600">
                          Retained surplus to date
                          <span className="ml-1 text-xs text-slate-400">
                            ({money(bs.total_income_to_date)} in, {money(bs.total_expenditure_to_date)} out)
                          </span>
                        </span>
                        <span className="font-mono" data-testid="retained-surplus">
                          {money(bs.retained_surplus)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t-2 border-slate-200 py-2 text-sm font-medium">
                        <span>Total funds</span>
                        <span className="font-mono" data-testid="total-funds">{money(bs.total_funds)}</span>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2" data-testid="reserve-split">
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-rose-600">
                          Restricted — not the trustees&apos; to spend
                        </p>
                        <p className="mt-1 font-mono text-xl text-rose-800"
                           data-testid="reserves-restricted">
                          {money(bs.reserves.restricted)}
                        </p>
                        {bs.reserves.funds.length > 0 && (
                          <ul className="mt-2 space-y-1 text-xs text-rose-700">
                            {bs.reserves.funds.map((f) => (
                              <li key={f.code} className="flex justify-between gap-2">
                                <span className="truncate">{f.name}</span>
                                <span className="font-mono">{money(f.balance)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-emerald-700">
                          Unrestricted reserves
                        </p>
                        <p className="mt-1 font-mono text-xl text-emerald-800"
                           data-testid="reserves-unrestricted">
                          {money(bs.reserves.unrestricted)}
                        </p>
                        <p className="mt-2 text-xs text-emerald-700">
                          {money(bs.reserves.unrestricted_cash_available)} of it is in cash
                          or the bank and available now.
                        </p>
                      </div>
                    </div>

                    {bs.reserves.overcommitted && (
                      <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-4"
                           data-testid="balance-sheet-overcommitted">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                        <p className="text-sm text-rose-800">
                          Restricted funds are owed more than the accounts hold — money given
                          for a purpose has been spent on something else.
                        </p>
                      </div>
                    )}

                    {bs.unclassified.length > 0 && (
                      <div data-testid="balance-sheet-unclassified">
                        <h3 className="text-base font-medium text-amber-700">Not yet classified</h3>
                        <p className="mb-1 text-xs text-slate-500">
                          These accounts have no type on the chart of accounts, so they cannot
                          be placed. They are shown rather than dropped.
                        </p>
                        {bs.unclassified.map((a) => <Row key={a.account_code} account={{ ...a, _from: '' }} amount={a.amount} />)}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {drill && (
          <DrillDown account={drill} start={start} end={end} onClose={() => setDrill(null)} />
        )}
      </div>
    </div>
  );
}
